<script setup lang="ts">
    import {onBeforeMount, ref, watch, computed } from "vue";
    import { useI18n } from "vue-i18n";
    import { storeToRefs } from "pinia";
    import {useRoute, useRouter} from "vue-router";
    import { useUserStore, useLocationsStore, useNotificationStore, useTenantStore } from "@/stores/";
    import Badge from 'primevue/badge';
    import Dropdown, { type DropdownChangeEvent } from 'primevue/dropdown';
    import { isInRole } from "@/utils";
    import { ROLES } from "@/config";
    import Sidebar from 'primevue/sidebar';
    import OverlayPanel from "primevue/overlaypanel";
    import PrimeMenu from "primevue/menu";
    

    
    const emit = defineEmits(["releaseNotes"]);
    const appversion = ref(import.meta.env.VITE_APP_VERSION);
    const { t } = useI18n();
    const appversiondate = ref(import.meta.env.VITE_APP_DATE);
    const userStore = useUserStore();
    const tenantStore = useTenantStore();
    const locationStore = useLocationsStore();
    const notificationStore = useNotificationStore();
    const { locationId } = storeToRefs(userStore);
    const { locations } = storeToRefs(locationStore);
    const router = useRouter();
    const route = useRoute();
    const currentPage = ref(route.path);
    const notificationsVisible = ref(false);
    const op = ref();
    const op2 = ref();
    const notificationCount = computed(() => notificationStore.notifications.filter(x => x.seenOn == null).length);
    var routes = computed(() => 
        router.getRoutes().filter((r:any) => 
        r.meta.inMenu && 
        r.meta.roles.some((x:string) => isInRole(x)) &&
        (r.meta.multiLocation == undefined || (r.meta.multiLocation == true && locations.value.length > 1)))
    );
    const isSmall = ref(false);
    window.addEventListener('resize', () => {
        if (window.innerWidth < 768) {
            isSmall.value = true;
        } else {
            isSmall.value = false;
        }
    });
    const toggleSmallLocationSelect = (event:any) => {
        op.value.toggle(event);
    }

    const changeLocation = (lid: DropdownChangeEvent) => {
        if (userStore.decodedAuth == null) return;
        userStore.decodedAuth!.lid = lid.value;
        userStore.setLocation(lid.value).then(() => {
            window.location.reload();
        });
    }
    const logout = () => {
        userStore.logout();
    }
    const showNotifications = () => {
        notificationsVisible.value = true;
    }
    const close = () => {
        notificationStore.setSeen();
        notificationsVisible.value = false;
    }
    const openNotification = (notification: any) => {
        close();
        if (notification.category?.toString() === '0') {
            router.push(notification.issueId ? `/issues?id=${notification.issueId}` : '/issues');
        } else if (notification.category?.toString() === '2') {
            router.push('/');
        }
    }

    onBeforeMount(() => {
        if (userStore.isAuthenticated) {
            notificationStore.list();
            tenantStore.getAll();
        }
        if (window.innerWidth < 768)
            isSmall.value = true;
    });

    watch(route, (to, from) => {
        currentPage.value = to.path;
    });

    const showReleaseNotes = () => {
        emit("releaseNotes");
    }
    const stillnew = () => {
        return new Date().getTime() < (new Date(appversiondate.value).getTime() + (3600000 * 24 * 5));
    } 
    const openTenants = (evt:any) => {
        op2.value.toggle(evt);
    }

    const tenants = computed(() => {
        return tenantStore.tenants.map((c) => ({
            label: c.name, 
            id: c.id,
            command: (t:any) => {
                console.log(t.item.id)
                userStore.setTenant(t.item.id);
            }
        }))
    })
</script>

<template>
    <aside v-if="userStore.isAuthenticated" id="mainmenu" class="fixed top-0 left-0 z-40 w-small md:w-64 h-screen transition-transform translate-x-0 md:translate-x-0 bg-white shadow-lg">
        <div class="flex flex-col h-full">
            <div class="text-center mt-3" :class="{'hidden': isSmall}">
                <div class="flex">
                    <img class="mx-auto h-12 w-auto" src="@/assets/logo.png" alt="CheckMate">
                    <div class="relative p-3 mr-1 relative">
                        <Badge :value="notificationCount" severity="danger" v-if="notificationCount > 0" @click="showNotifications" class="p-overlay-badge fixed right-0 mr-1" />
                        <i class="fa fa-bell" style="font-size: 2rem" @click="showNotifications" />
                    </div>
                </div>
                <div>{{ userStore.tenant }} 
                    <button v-if="tenantStore.tenants.length > 1" severity="secondary" text rounded aria-label="More" @click="openTenants"><i class='fas fa-caret-down' /></button>
                    <PrimeMenu ref="op2" :model="tenants" :popup="true" ></PrimeMenu>
                </div>
            </div>
            <div class="relative" :class="{'hidden': !isSmall}">
                <Badge :value="notificationCount" severity="danger" v-if="notificationCount > 0" @click="showNotifications" class="p-overlay-badge fixed right-2" />
                <img src="@/assets/icon.jpg" alt="CheckMate" class="mx-auto h-12 w-auto" />
            </div>
            <div class="text-center p3 mt-3 px-4"  v-if="locations.length > 1">
                <Dropdown
                    v-if="!isSmall"
                    filter
                    :options="locations.filter(x => x.isEnabled).map((s:any) => ({text: s.name, value: s.id})).sort((x,y) => x.text.localeCompare(y.text))"
                    v-model="locationId"
                    optionLabel="text"
                    optionValue="value"
                    class="w-full z-top"
                    @change="changeLocation"
                    appendTo="body"
                />
                <div v-if="isSmall">
                    <span class="text-xs line-clamp-2" :title="locations?.find(x => x.id == locationId)?.name" @click="toggleSmallLocationSelect">
                        {{ locations?.find(x => x.id == locationId)?.name }}
                    </span>
                    <OverlayPanel ref="op">
                        <div>
                            <div>{{ t('menu.location') }}:</div>
                            <div>
                                <Dropdown
                                    filter
                                    :options="locations.map((s:any) => ({text: s.name, value: s.id})).sort((x,y) => x.text.localeCompare(y.text))"
                                    v-model="locationId"
                                    optionLabel="text"
                                    optionValue="value"
                                    class="w-full"
                                    @change="changeLocation"
                                />
                            </div>
                        </div>
                    </OverlayPanel>
                </div>
            </div>
                <div class="-auto mt-4">
                    <ul class="list-none m-0" :class="{'px-1': isSmall, 'px-4': !isSmall}">
                        <li v-for="route in routes" v-bind:key="route.name">
                            <router-link
                                :to="route.path"
                                class="flex items-center cursor-pointer p-1 gap-2 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700 duration-200 transition-colors"
                                :class="{
                                    'bg-primary text-white': currentPage===route.path,
                                    'text-xs': isSmall,
                                    'flex-col': isSmall,
                                    'gap-0': isSmall,
                                    'p-2': !isSmall,
                                    }">
                                <i :title="route.name?.toString()" class="text-lg md:text-base" :class="route.meta.icon"></i>
                                <span >{{route.name}}</span>
                            </router-link>
                        </li>
                    </ul>
                </div>
                <div class="mt-auto overflow-y-auto">
                    <hr class="mb-3 mx-3 border-t-1  border-surface-200 dark:border-surface-700" />
                    <ul class="list-none m-0" :class="{'px-1': isSmall, 'px-4': !isSmall}">
                        <li v-if="isInRole(ROLES.CHECKMATE)">
                            <router-link
                                to="/accounts"
                                class="flex items-center cursor-pointer p-1 gap-2 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700 duration-200 transition-colors"
                                :class="{
                                    'bg-primary text-white': currentPage==='/accounts',
                                    'text-xs': isSmall,
                                    'flex-col': isSmall,
                                    'gap-0': isSmall,
                                    'p-1': !isSmall,
                                }">
                                <i title="Accounts" class="fa fa-boxes text-lg md:text-base"></i>
                                <span>{{ t('menu.accounts') }}</span>
                            </router-link>
                        </li>
                        <li v-if="isInRole(ROLES.CHECKMATE) || isInRole(ROLES.ADMIN) || isInRole(ROLES.OFFICE)">
                            <router-link 
                                to="/locations" 
                                class="flex items-center cursor-pointer p-1 gap-2 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700 duration-200 transition-colors" 
                                :class="{
                                    'bg-primary text-white': currentPage==='/locations',
                                    'text-xs': isSmall,
                                    'flex-col': isSmall,
                                    'gap-0': isSmall,
                                    'p-2': !isSmall,
                                    }">
                                <i title="Locations" class="fa fa-location-dot text-lg md:text-base"></i>
                                <span>{{ t('menu.locations') }}</span>
                            </router-link>
                        </li>
                        <li v-if="isInRole(ROLES.CHECKMATE) || isInRole(ROLES.ADMIN) || isInRole(ROLES.OFFICE)">
                            <router-link 
                                to="/users" 
                                class="flex items-center cursor-pointer p-1 gap-2 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700 duration-200 transition-colors" 
                                :class="{
                                    'bg-primary text-white': currentPage==='/users',
                                    'text-xs': isSmall,
                                    'flex-col': isSmall,
                                    'gap-0': isSmall,
                                    'p-2': !isSmall,
                                    }"
                                >
                                <i title="Users" class="fa fa-users text-lg md:text-base"></i>
                                <span>{{ t('menu.users') }}</span>
                            </router-link>
                        </li>
                        <li v-if="isInRole(ROLES.ADMIN)">
                            <router-link
                                to="/settings"
                                class="flex items-center cursor-pointer p-1 gap-2 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700 duration-200 transition-colors"
                                :class="{
                                    'bg-primary text-white': currentPage==='/settings',
                                    'text-xs': isSmall,
                                    'flex-col': isSmall,
                                    'gap-0': isSmall,
                                    'p-2': !isSmall,
                                }">
                                <i title="Settings" class="fa fa-cog text-lg md:text-base"></i>
                                <span>{{ t('menu.settings') }}</span>
                            </router-link>
                        </li>
                         <li>
                            <router-link
                                to="/my-profile"
                                class="flex items-center cursor-pointer p-1 gap-2 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700 duration-200 transition-colors"
                                :class="{
                                    'bg-primary text-white': currentPage==='/my-profile',
                                    'text-xs': isSmall,
                                    'flex-col': isSmall,
                                    'gap-0': isSmall,
                                    'p-2': !isSmall,
                                }">
                                <i title="My Profile" class="fa fa-user text-lg md:text-base"></i>
                                <span>{{ t('menu.myProfile') }}</span>
                            </router-link>
                        </li>
                        <li>
                            <a 
                            target="_blank" 
                            href="https://checkmate.software/training-videos/" 
                            class="flex items-center cursor-pointer p-1 gap-2 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700 duration-200 transition-colors" 
                            :class="{
                                'text-xs': isSmall,
                                'flex-col': isSmall,
                                'gap-0': isSmall,
                                'p-2': !isSmall
                                }"
                            >
                                <i title="Help" class="fas fa-question-circle text-lg md:text-base"></i>
                                <span>{{ t('menu.help') }}</span>
                            </a>
                        </li>
                        <li>
                            <a
                                href="javascript:void(0)"
                                @click="logout"
                                class="flex items-center cursor-pointer p-1 gap-2 rounded-md text-surface-700 dark:text-surface-0/80 hover:bg-surface-100 dark:hover:bg-surface-700 duration-200 transition-colors"
                                :class="{
                                'text-xs': isSmall,
                                'flex-col': isSmall,
                                'gap-0': isSmall,
                                'p-2': !isSmall
                                }">
                                <i title="Logout" class="fas fa-right-from-bracket text-lg md:text-base"></i>
                                <span>{{ t('menu.logout') }}</span>
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="text-center">
                <small style="font-size: 10px;" @click="showReleaseNotes">
                    {{ t('menu.version') }}: {{ appversion }}
                    <span v-if="stillnew()" class="new-indicator"></span>

                </small>
            </div>           
        </div>

        <Sidebar v-model:visible="notificationsVisible" @hide="close" :header="t('menu.notification')" style="width: 35vw" position="right">
            <div v-for="notification in notificationStore.notifications" :key="notification.createdOn" class="p-3 border-b border-surface-200 dark:border-surface-700">
                <div :class="{'new-notification': notification.seenOn == null}">
                    <a v-if="notification.category?.toString() == '0' || notification.category?.toString() == '2'"
                       class="cursor-pointer hover:underline"
                       @click="openNotification(notification)">{{ notification.message }}</a>
                    <span v-if="notification.category?.toString() == '1'">{{ notification.message }}</span>
                </div>
            </div>
            <div v-if="notificationStore.notifications.length == 0">
                <div class="p-3 text-center">{{ t('menu.noNotifications') }}</div>
            </div>
        </Sidebar>
    </aside>
</template>

<style>
    .new-notification {
        font-weight: bold;
    }
    .new-indicator{
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: red;
        display:inline-block;
        margin-bottom:.5px;
    }
    .z-top{ z-index:9999!important}

</style>